<template>
  <div :class="[baseCss, $dark ? `${baseCss}-dark` : null, 'outline-none']" tabindex="0" @keyup.prevent.left="arrowNavigation ? scroll(slide - 1) : null" @keyup.prevent.right="arrowNavigation ? scroll(slide + 1) : null" @mouseover="onMouseOver" @mouseout="onMouseOut">
    <div :class="['relative']">
      <div :class="['overflow-hidden']">
        <ul :class="['inline-list', 'whitespace-nowrap', 'transition-all', `duration-${slideDuration}`, `ease-${easing}`]" :style="style">
          <slot />
        </ul>
      </div>
      <radio-group :icon-provider="iconProvider" :icon-blank="dotsIconBlank" :icon-checked="dotsIconChecked" :icon-size="dotsSize" v-if="['top', 'bottom'].includes(dots)" :name="uuid" v-model="slide" :vertical="false" align="center" :class="['absolute', 'w-full', `${dots}-0`]">
        <radio :icon-color="uiColor" :icon-color-lightness="uiColorLightness" :value="index - 1" v-for="index in slides" :key="index" />
      </radio-group>
      <table-layout v-if="buttons" :class="['absolute', 'select-none', `${baseCss}-buttons-container`, 'transition-all', `duration-${slideDuration}`, `ease-${easing}`, 'w-full', 'top-0', 'h-full', 'pointer-events-none']" y-align="middle">
        <row>
          <cell :class="[`${baseCss}-buttons`, !endless && slideInt === 0 ? ['disabled', 'pointer-events-none'] : ['cursor-pointer', 'pointer-events-auto']]" @click="scroll(slideInt - 1)">
            <icon :provider="iconProvider" :name="iconLeft" :size="buttonsSize" :color="uiColor" :color-lightness="uiColorLightness" />
          </cell>
          <cell full-width />
          <cell :class="[`${baseCss}-buttons`, !endless && slideInt === slides - 1 ? ['disabled', 'pointer-events-none'] : ['cursor-pointer', 'pointer-events-auto']]" @click="scroll(slideInt + 1)">
            <icon :provider="iconProvider" :name="iconRight" :size="buttonsSize" :color="uiColor" :color-lightness="uiColorLightness" />
          </cell>
        </row>
      </table-layout>
    </div>
    <template v-if="count">
      <divider v-if="countDivider" />
      <container :align="countAlign" :class="[`text-${countSize}`, setTextColor('countColor', 'countColorLightness'), 'p-1', 'select-none']">{{ slideInt + 1 }}{{ countDelimiter }}{{ slides }}</container>
    </template>
  </div>
</template>
<script>
import methods from '../../mixins/methods'

export default {
  name: 'Carousel',
  mixins: [
    methods
  ],
  data () {
    return {
      slide: 0,
      slides: 0,
      dirty: false,
      uuid: this.$uuidv4(),
      autoplayInterval: null,
      baseCss: this.componentBaseCss('carousel', [])
    }
  },
  props: {
    autoplay: {
      type: Number
    },
    endless: {
      type: Boolean
    },
    defaultSlide: {
      type: Number,
      default: 0
    },
    countDivider: {
      type: Boolean,
      default: true
    },
    count: {
      type: Boolean,
      default: true
    },
    countColor: {
      type: String
    },
    countColorLightness: {
      type: Number
    },
    countAlign: {
      type: String,
      default: 'right',
      validator: value => {
        return [
          'left',
          'center',
          'right'
        ].includes(value)
      }
    },
    countDelimiter: {
      type: String,
      default: ' / '
    },
    countSize: {
      type: String,
      default: 'xs',
      validator: value => {
        return [
          'xs',
          'sm',
          'base',
          'lg',
          'xl'
        ].includes(value)
      }
    },
    aspectRatio: {
      type: String,
      default: '5/3'
    },
    dotsIconBlank: {
      type: String,
      default: 'checkbox-blank-circle-outline'
    },
    dotsIconChecked: {
      type: String,
      default: 'checkbox-blank-circle'
    },
    iconProvider: {
      type: String,
      default: 'mdi'
    },
    dots: {
      type: String,
      default: 'bottom',
      validator: value => {
        return [
          'top',
          'bottom',
          'none'
        ].includes(value)
      }
    },
    buttonsSize: {
      type: Number,
      default: 2.5
    },
    dotsSize: {
      type: Number,
      default: 1
    },
    slideDuration: {
      type: Number,
      default: 300,
      validator: value => {
        return [
          75,
          100,
          150,
          200,
          300,
          500,
          700,
          1000
        ].includes(value)
      }
    },
    easing: {
      type: String,
      default: 'in',
      validator: value => {
        return [
          'in',
          'out',
          'in-out',
          'linear'
        ].includes(value)
      }
    },
    buttons: {
      type: Boolean,
      default: true
    },
    iconLeft: {
      type: String,
      default: 'chevron-left'
    },
    iconRight: {
      type: String,
      default: 'chevron-right'
    },
    uiColor: {
      type: String
    },
    uiColorLightness: {
      type: Number
    },
    arrowNavigation: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    autoplay () {
      this.autoplay ? this.init() : clearInterval(this.autoplayInterval)
    },
    slide () {
      this.$emit('scroll', this.slideInt)
    }
  },
  computed: {
    slideInt () {
      return parseInt(this.slide, 10)
    },
    style () {
      const slide = this.slide
      const left = ((slide === 0 ? 0 : slide * 100) * -1) + '%'
      return {
        aspectRatio: this.aspectRatio,
        transform: `translateX(${left})`
      }
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    init () {
      this.slides = this.getSlides().length
      this.slide = this.defaultSlide
      this.initAutoplay()
    },
    onMouseOver () {
      if (this.arrowNavigation) {
        this.$el.focus()
        this.dirty = true
      }
    },
    onMouseOut () {
      if (this.arrowNavigation) {
        this.$el.blur()
        this.dirty = false
      }
    },
    initAutoplay () {
      const autoplay = this.autoplay
      const endless = this.endless
      if (autoplay && endless) {
        this.autoplayInterval = setInterval(() => {
          if (!this.dirty) {
            this.scroll(this.slide + 1)
          }
        }, autoplay)
      } else if (autoplay && !endless) {
        console.error(`<${this.baseCss} />`, 'you cannot use "autoplay" when the "endless" prop is false')
      }
    },
    scrollEndless (index) {
      if (index > -1 && index < this.slides) {
        this.slide = index
      } else if (index === this.slides) {
        this.slide = 0
      } else if (index === -1) {
        this.slide = this.slides - 1
      }
    },
    scroll (index) {
      const endless = this.endless
      if (endless) {
        this.scrollEndless(index)
      } else {
        if (index > -1 && index < this.slides) {
          this.slide = index
        }
      }
    },
    getSlides () {
      return this.$el.querySelectorAll('.bcq-carousel-slide')
    },
    getInput () {
      return this.$el.querySelector('input[type="text"]')
    }
  }
}
</script>
<style>
.bcq-carousel .bcq-carousel-buttons
{
  background: var(--semi-transparent-light);
}

.bcq-carousel-dark .bcq-carousel-buttons
{
  background: var(--semi-transparent-dark);
}

.bcq-carousel .bcq-carousel-buttons-container
{
  opacity: 0;
}

.bcq-carousel:hover .bcq-carousel-buttons-container
{
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/bcq-ui/components/carousel/carousel.vue